import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAdicionales } from '@/presentation/hooks/useAdicionales';
import { useToast } from '@/presentation/contexts/ToastContext';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { ToggleAdicionalStatusAction, DeleteAdicionalAction } from '@/application/actions';
import { Button, Input } from '@/presentation/components/common';
import { Adicional, ADICIONAL_TIPO_LABELS, type AdicionalTipo } from '@/domain/entities/Adicional';
import {
  CreateAdicionalModal,
  EditAdicionalModal,
} from '@/presentation/components/adicionales';
import { useAuth } from '@/presentation/contexts/AuthContext';

const tipoBadgeClass = (tipo: AdicionalTipo) => {
  if (tipo === 'COOFIT_WHATSAPP_BOT') {
    return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200';
  }
  if (tipo === 'WHATSAPP_MENSAJES') {
    return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
};

export const AdicionalesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const { canAdminister } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTipo, setCreateTipo] = useState<AdicionalTipo>('COOFIT_WHATSAPP_BOT');
  const [editingAdicional, setEditingAdicional] = useState<Adicional | null>(null);

  const activoFilter =
    statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined;

  const { adicionales, isLoading, error, refetch } = useAdicionales(
    searchTerm.trim() || undefined,
    activoFilter,
  );

  const groups = useMemo(() => {
    const bot = adicionales.filter((a) => a.tipo === 'COOFIT_WHATSAPP_BOT');
    const extras = adicionales.filter(
      (a) => a.tipo === 'CONSULTORIO_EXTRA' || a.tipo === 'PROFESIONAL_EXTRA',
    );
    return { bot, extras };
  }, [adicionales]);

  const handleToggleStatus = async (adicional: Adicional) => {
    const action = adicional.isActive()
      ? t('pages.adicionales.deactivate')
      : t('pages.adicionales.activate');
    if (
      !window.confirm(t('pages.adicionales.toggleConfirm', { action, name: adicional.nombre }))
    ) {
      return;
    }
    try {
      const toggleAction = container.get<ToggleAdicionalStatusAction>(
        TYPES.ToggleAdicionalStatusAction,
      );
      await toggleAction.execute(adicional.id);
      success(
        adicional.isActive()
          ? t('pages.adicionales.deactivatedSuccess')
          : t('pages.adicionales.activatedSuccess'),
      );
      refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const handleDelete = async (adicional: Adicional) => {
    if (
      !window.confirm(
        `¿Eliminar el adicional "${adicional.nombre}"?\nTambién se quita de las clínicas que lo tengan asignado.`,
      )
    ) {
      return;
    }
    try {
      const deleteAction = container.get<DeleteAdicionalAction>(TYPES.DeleteAdicionalAction);
      await deleteAction.execute(adicional.id);
      success(`Adicional "${adicional.nombre}" eliminado`);
      refetch();
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    }
  };

  const openCreate = (tipo: AdicionalTipo) => {
    setCreateTipo(tipo);
    setShowCreateModal(true);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const getStatusFilterLabel = (value: typeof statusFilter): string => {
    if (value === 'active') return t('pages.adicionales.active');
    if (value === 'inactive') return t('pages.adicionales.inactive');
    return t('pages.adicionales.all');
  };

  const activeFilterChips = [
    ...(searchTerm.trim()
      ? [
          {
            key: 'search',
            label: `${t('common.search')}: ${searchTerm.trim()}`,
            onRemove: () => setSearchTerm(''),
          },
        ]
      : []),
    ...(statusFilter !== 'all'
      ? [
          {
            key: 'status',
            label: `${t('pages.adicionales.status')}: ${getStatusFilterLabel(statusFilter)}`,
            onRemove: () => setStatusFilter('all'),
          },
        ]
      : []),
  ];

  const renderCard = (adicional: Adicional) => (
    <article
      key={adicional.id}
      className={`rounded-xl border bg-white p-4 shadow-sm dark:bg-gray-800 ${
        adicional.isActive()
          ? 'border-gray-200 dark:border-gray-700'
          : 'border-gray-300 opacity-70 dark:border-gray-600'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-white">{adicional.nombre}</h3>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tipoBadgeClass(adicional.tipo)}`}
            >
              {ADICIONAL_TIPO_LABELS[adicional.tipo]}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                adicional.isActive()
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              {adicional.isActive()
                ? t('pages.adicionales.active')
                : t('pages.adicionales.inactive')}
            </span>
          </div>
          {adicional.descripcion ? (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{adicional.descripcion}</p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600 dark:text-gray-300">
            {adicional.isMessagePack() && adicional.cupoMensajes != null ? (
              <span>
                <strong>{adicional.cupoMensajes.toLocaleString('es-AR')}</strong> mensajes / pack
              </span>
            ) : null}
            {!adicional.isMessagePack() && adicional.unidad ? (
              <span>
                Unidad: <strong>{adicional.unidad}</strong>
              </span>
            ) : null}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            ${adicional.precioUnitario.toLocaleString('es-AR')}
          </div>
          <div className="text-xs text-gray-500">
            {adicional.isMessagePack() ? 'por pack' : 'precio mensual'}
          </div>
        </div>
      </div>
      {canAdminister ? (
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setEditingAdicional(adicional)}>
            Editar precio
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(adicional)}>
            {adicional.isActive()
              ? t('pages.adicionales.deactivate')
              : t('pages.adicionales.activate')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 dark:text-red-400"
            onClick={() => handleDelete(adicional)}
          >
            Eliminar
          </Button>
        </div>
      ) : null}
    </article>
  );

  const renderSection = (
    title: string,
    subtitle: string,
    items: Adicional[],
    createTipo: AdicionalTipo | null,
  ) => (
    <section className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
        {canAdminister && createTipo ? (
          <Button variant="secondary" size="sm" onClick={() => openCreate(createTipo)}>
            + Cargar precio
          </Button>
        ) : null}
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-600">
          Todavía no hay packs cargados.
          {canAdminister && createTipo ? (
            <>
              {' '}
              <button
                type="button"
                className="text-teal-600 underline dark:text-teal-400"
                onClick={() => openCreate(createTipo)}
              >
                Cargar el primero
              </button>
            </>
          ) : null}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">{items.map(renderCard)}</div>
      )}
    </section>
  );

  if (isLoading && adicionales.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center p-6">
        <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="panel-page">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.adicionales.title')}</h1>
          <p className="panel-page-subtitle hidden sm:block">{t('pages.adicionales.subtitle')}</p>
        </div>
        <div className="flex w-full flex-wrap gap-1.5 sm:w-auto">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/comercial')}
            className="flex-1 sm:flex-none"
          >
            {t('menu.comercial')}
          </Button>
          {canAdminister ? (
            <Button
              variant="primary"
              size="sm"
              onClick={() => openCreate('COOFIT_WHATSAPP_BOT')}
              className="flex-1 sm:flex-none"
            >
              {t('pages.adicionales.create')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
          <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-[minmax(18rem,1fr)_minmax(10rem,0.35fr)]">
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('pages.adicionales.searchPlaceholder')}
              className="h-8 py-1 text-xs"
              aria-label={t('common.search')}
              title={t('common.search')}
            />
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                aria-label={t('pages.adicionales.filter')}
                title={t('pages.adicionales.filter')}
              >
                <option value="all">{t('pages.adicionales.all')}</option>
                <option value="active">{t('pages.adicionales.active')}</option>
                <option value="inactive">{t('pages.adicionales.inactive')}</option>
              </select>
            </div>
          </div>

          <button
            type="button"
            onClick={clearFilters}
            disabled={activeFilterChips.length === 0}
            className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white lg:w-8"
            title={t('pages.adicionales.clearFilters')}
            aria-label={t('pages.adicionales.clearFilters')}
          >
            <svg
              className="h-3.5 w-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.75}
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10"
              />
            </svg>
            <span className="ml-2 text-sm lg:hidden">{t('pages.adicionales.clearFilters')}</span>
          </button>
        </div>

        {activeFilterChips.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={chip.onRemove}
                className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                title={chip.label}
              >
                <span className="truncate">{chip.label}</span>
                <span className="text-sm leading-none" aria-hidden>
                  ×
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <p className="text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      ) : null}

      {renderSection(
        'Coofit Agenda WhatsApp',
        'Bot que agenda, reprograma y cancela turnos por chat. Se asigna por clínica (requiere Coofit Conexión).',
        groups.bot,
        'COOFIT_WHATSAPP_BOT',
      )}

      {renderSection(
        'Extras de plan',
        'Consultorio o profesional extra. Precio de catálogo. La clínica puede sumarlo desde Coofit (cobro en la próxima factura) o se asigna a mano en la ficha comercial.',
        groups.extras,
        null,
      )}

      {showCreateModal ? (
        <CreateAdicionalModal
          isOpen={showCreateModal}
          initialTipo={createTipo}
          lockTipo
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      ) : null}

      {editingAdicional ? (
        <EditAdicionalModal
          key={editingAdicional.id}
          isOpen={!!editingAdicional}
          adicional={editingAdicional}
          onClose={() => setEditingAdicional(null)}
          onSuccess={() => {
            setEditingAdicional(null);
            refetch();
          }}
        />
      ) : null}
    </div>
  );
};
